'use client';

import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import Button from '@/components/Button';
import { track } from '@/lib/analytics';
import { devDefaults } from '@/lib/devDefaults';
import { useCreateProject } from '@/lib/react-query/ProjectQueries';

import Step1Project from './steps/Step1Project';
import Ste2Inspirations from './steps/Step2Inspirations';
import Step3ClientInfo from './steps/Step3ClientInfo';

export type FormData = {
  // Step 1
  description: string;
  size: string;
  bodyPlacement: string;
  customPlacement: string;
  // Step 2
  inspirationText: string;
  inspirationImages: Array<File>;
  inspirationUrls: string;
  // Step 3
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const TOTAL_STEPS = 3;

export default function FormProject() {
  const navigate = useNavigate();
  const createProjectMutation = useCreateProject();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    description: '',
    size: '',
    bodyPlacement: '',
    customPlacement: '',
    inspirationText: '',
    inspirationImages: [],
    inspirationUrls: '',
    firstName: devDefaults.first_name,
    lastName: devDefaults.last_name,
    email: devDefaults.email,
    phone: devDefaults.phone,
  });

  const updateFormData = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(
          formData.description.trim() &&
          formData.size &&
          formData.bodyPlacement &&
          (formData.bodyPlacement !== 'Autre' || formData.customPlacement.trim())
        );
      case 2:
        return true;
      case 3: {
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
        const phoneValid = /^(\+33|0)[1-9](\d{8})$/.test(formData.phone.replace(/\s/g, ''));
        return !!(
          formData.firstName.trim() &&
          formData.lastName.trim() &&
          emailValid &&
          phoneValid
        );
      }
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (currentStep < TOTAL_STEPS && validateStep(currentStep)) {
      if (currentStep === 1) {
        track('ProjetStep1Complete', {
          size: formData.size,
          bodyPlacement: formData.bodyPlacement,
        });
      } else if (currentStep === 2) {
        track('ProjetStep2Complete', {
          hasImages: String(formData.inspirationImages.length > 0),
          hasUrls: String(formData.inspirationUrls.trim().length > 0),
        });
      }
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      return;
    }

    try {
      // Convert File objects to base64 data URLs
      const imageDataUrls: Array<string> = [];

      for (const file of formData.inspirationImages) {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        imageDataUrls.push(dataUrl);
      }

      // Submit project using mutation
      const result = await createProjectMutation.mutateAsync({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        description: formData.description,
        size: formData.size,
        bodyPlacement: formData.bodyPlacement,
        customPlacement: formData.customPlacement,
        inspirationText: formData.inspirationText,
        inspirationUrls: formData.inspirationUrls,
        inspirationImages: imageDataUrls,
      });

      // Redirect to confirmation page with project ID
      if (result.data.project_id) {
        track('ProjetFormSubmit');
        navigate({
          to: '/tatouage/confirmation',
          search: { id: result.data.project_id },
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Error toast is already handled by the mutation
    }
  };

  const isCurrentStepValid = validateStep(currentStep);

  return (
    <div className="rounded-md bg-white p-8 shadow-sm">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="mb-2 flex justify-between text-sm font-medium text-gray-600">
          <span>
            Étape {currentStep} sur {TOTAL_STEPS}
          </span>
          <span>{Math.round((currentStep / TOTAL_STEPS) * 100)}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full bg-emerald-600 transition-all duration-500"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      {/* Step indicators */}
      <div className="mb-8 flex justify-between">
        {[
          { num: 1, label: 'Projet' },
          { num: 2, label: 'Inspirations' },
          { num: 3, label: 'Infos' },
        ].map((step) => (
          <div key={step.num} className="flex flex-col items-center">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full border-2 font-semibold transition-all ${
                currentStep >= step.num
                  ? 'border-emerald-600 bg-emerald-600 text-white'
                  : 'border-gray-300 bg-white text-gray-400'
              }`}
            >
              {step.num}
            </div>
            <span
              className={`mt-2 text-xs font-medium ${
                currentStep >= step.num ? 'text-emerald-600' : 'text-gray-400'
              }`}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>

      {/* Form steps */}
      <div className="min-h-[400px]">
        {currentStep === 1 && <Step1Project formData={formData} updateFormData={updateFormData} />}
        {currentStep === 2 && (
          <Ste2Inspirations formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 3 && (
          <Step3ClientInfo formData={formData} updateFormData={updateFormData} />
        )}
      </div>

      {/* Navigation buttons */}
      <div className="mt-8 flex justify-between">
        <Button
          onClick={prevStep}
          disabled={currentStep === 1}
          color="secondary"
          size="lg"
          className="border-emerald-600 text-emerald-600 hover:border-emerald-700 hover:text-emerald-700"
        >
          Précédent
        </Button>
        {currentStep < TOTAL_STEPS ? (
          <Button
            onClick={nextStep}
            disabled={!isCurrentStepValid}
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Suivant
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!isCurrentStepValid || createProjectMutation.isPending}
            isLoading={createProjectMutation.isPending}
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {createProjectMutation.isPending ? 'Envoi en cours...' : 'Envoyer ma demande'}
          </Button>
        )}
      </div>
    </div>
  );
}
